import { CardContainer } from '../CardContainer';
import type { MonthRevenue } from '../../services/api';

const MONTH_NAMES: Record<string, string> = {
  '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
  '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
  '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre',
};

const formatCurrency = (amount: number) =>
  `Q ${Math.abs(amount).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

interface Props {
  months: MonthRevenue[];
}

interface TableRow {
  label: string;
  revenue: number;
  delta: number;
  changePct: number | null;
}

export const RevenueTable = ({ months }: Props) => {
  // Only include months up to and including the current month (future months have no data),
  // then take the last 6 of those and reverse so newest appears first.
  const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const upToCurrent = months.filter((m) => m.month <= currentMonthKey);
  const last6 = upToCurrent.slice(-6).reverse();

  const rows: TableRow[] = last6.map((m, i) => {
    const prev = last6[i + 1];
    const delta = prev ? m.revenue - prev.revenue : 0;
    const changePct = prev && prev.revenue > 0 ? (delta / prev.revenue) * 100 : null;
    return {
      label: MONTH_NAMES[m.month.slice(5, 7)] ?? m.month,
      revenue: m.revenue,
      delta,
      changePct,
    };
  });

  return (
    <CardContainer>
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-slate-900 text-lg font-bold">Últimos 6 Meses</h3>
          <p className="text-slate-500 text-xs">Comparativa directa mensual</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Mes</th>
              <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Ingresos</th>
              <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">
                vs Anterior
              </th>
              <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">
                Cambio %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((row, i) => {
              const isPositive = row.delta >= 0;
              return (
                <tr
                  key={i}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="py-4 text-sm font-bold text-slate-900">{row.label}</td>
                  <td className="py-4 text-sm text-slate-600 font-medium">
                    {formatCurrency(row.revenue)}
                  </td>
                  <td className="py-4 text-sm text-slate-600 text-right">
                    {row.changePct !== null
                      ? `${isPositive ? '+' : '-'}${formatCurrency(Math.abs(row.delta))}`
                      : '—'}
                  </td>
                  <td className="py-4 text-right">
                    {row.changePct !== null ? (
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg ${
                          isPositive
                            ? 'text-emerald-600 bg-emerald-50'
                            : 'text-rose-600 bg-rose-50'
                        }`}
                      >
                        {isPositive ? '↑' : '↓'}
                        {Math.abs(row.changePct).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardContainer>
  );
};
